import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppConfigService, SecurityService } from 'common';
import { CardDataService, ICard, RootLayoutModule } from 'root-layout';
import { SenderIdCardData } from '../../interfaces/card-data.interface';
import { RoleService } from '../../services/role.service';
import { ProfileService } from '../../services/profile.service';
import { AccountApiService } from '../../services/account-api.service';
import { DialogService } from '../../services/dialog.service';
import { NotificationService } from '../../services/notification.service';
import { Subject } from 'rxjs';
import { filter, lastValueFrom, switchMap, takeUntil, tap } from 'rxjs';
import { MatDialogModule } from '@angular/material/dialog';
import { MobileWarningService } from '../../services/mobile-warning.service';
import { ThirdPartiesApiService} from '../../services/third-parties-api.service';

@Component({
  selector: 'si-home',
  standalone: true,
  imports: [RootLayoutModule, MatDialogModule],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
})
export class HomeComponent implements OnInit, OnDestroy {
  route = inject(ActivatedRoute);
  router = inject(Router);
  securityService = inject(SecurityService);
  roleService = inject(RoleService);
  cardsService = inject(CardDataService);
  profileService = inject(ProfileService);
  dialogService = inject(DialogService);
  accountApiService = inject(AccountApiService);
  thirdPartyApiService = inject(ThirdPartiesApiService);
  notificationService = inject(NotificationService);
  private env = inject(AppConfigService);

  private _destroy$ = new Subject<void>();

  cards: ICard<SenderIdCardData>[] = [];

  get isLoggedIn(): boolean {
    return this.securityService.isLoggedIn();
  }

  async ngOnInit(): Promise<void> {
    this.cards = this.getCards();

    this.roleService.role$.pipe(
      takeUntil(this._destroy$),
      tap(() => (this.cards = this.getCards()))
    );

    const showPendingAuthorisations = this.isLoggedIn && !this.roleService.isAdmin && !this.route.snapshot.queryParams.reason;

    if (showPendingAuthorisations) {
      try {
        const count = await lastValueFrom(this.accountApiService.getAllPendingAuthorisationListCount$());

        if (count) this.openPendingSenderIdsDialog();
      } catch {}
    }
  }

  getCards() {
    return this.cardsService.cards.comreg.home.filter((card: ICard<SenderIdCardData>) => {
      if (this.isLoggedIn) {
        return card.data.loggedIn && card.data.roles.includes(this.roleService.role);
      } else {
        return !card.data.loggedIn;
      }
    });
  }

  private openPendingSenderIdsDialog() {
    this.dialogService
      .openAllPendingSenderIds()
      .afterClosed()
      .pipe(
        filter((value) => value),
        switchMap((items) =>
          this.accountApiService.addAllSenderIdPendingAuthorisations$({
            reason: '',
            items,
          })
        ),
        tap(() => this.notificationService.showNotification({ message: 'Sender IDs authorised' }))
      )
      .subscribe();
  }

  handleCardClick(card: ICard<SenderIdCardData>) {
    switch (card.data.pageName) {
      case 'organisationDetails':
        this.router.navigate([`registrations/${this.profileService.shortAccountId}`]);
        break;

      case 'FAQ':
        window.open('https://www.comreg.ie/senderid', '_blank');
        break;

      case 'sidoHelp':
        window.open(this.getPublicDownloadUrl('SMS Sender ID Registry SIDO Guide ver 1-1.pdf'), '_blank');
        break;

      case 'paHelp':
        window.open(this.getPublicDownloadUrl('SMS Sender ID Registry PA MSP Guide ver 1-1.pdf'), '_blank');
        break;

      case 'generalHelp':
        window.open('https://www.comreg.ie/media/2025/06/SMS-Sender-ID-Registry-Public-Guide-ver-1-2.pdf', '_blank');
        break;

      default:
        break;
    }
  }

  getPublicDownloadUrl(filename: string) {
    return `${this.env.config.gatewayUrl}/senderidregistry/public/files/view/${filename}`;
  }

  ngOnDestroy(): void {
    this._destroy$.next();
  }
}
